// Confidence Check wizard — Learner+ tiers, 3–5 minute retest at day 14.
// 5–7 rephrased questions + self-assessment prompt + results view.
//
// Source: CogniPrint_Recalibration_Cadence_Research_v2.md §2.3b,
// CogniPrint_Recalibration_Dev_Plan_v1.md §3.6 (REC-022).

import { useEffect, useState } from 'react';
import { X, ArrowRight, Check } from 'lucide-react';
import {
  startConfidenceCheck,
  completeConfidenceCheck,
  type CheckQuestion,
  type CheckSelfAssessment,
  type CheckAnswer,
  type DimensionChange,
} from '@shared/services/recalibration-api';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Phase =
  | 'loading'
  | 'questions'
  | 'self_assessment'
  | 'submitting'
  | 'results'
  | 'error';

export function CheckWizard({ open, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<CheckQuestion[]>([]);
  const [selfAssessment, setSelfAssessment] = useState<CheckSelfAssessment | null>(null);
  const [answers, setAnswers] = useState<Record<string, CheckAnswer>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [saResponse, setSaResponse] = useState('');
  const [summary, setSummary] = useState('');
  const [changes, setChanges] = useState<DimensionChange[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function start() {
      setPhase('loading');
      setError(null);
      try {
        trackEvent({
          name: ANALYTICS_EVENTS.RECAL_CHECK_STARTED,
          properties: getBaseProperties(),
        });
        const result = await startConfidenceCheck();
        if (cancelled) return;
        setSessionId(result.sessionId);
        setQuestions(result.questions);
        setSelfAssessment(result.selfAssessment);
        setActiveIndex(0);
        setAnswers({});
        setSaResponse('');
        setPhase('questions');
      } catch (err) {
        if (cancelled) return;
        setPhase('error');
        setError(err instanceof Error ? err.message : 'Unexpected error');
      }
    }

    void start();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const activeQuestion = questions[activeIndex];
  const activeAnswer = activeQuestion ? answers[activeQuestion.id] : null;
  const hasSelection =
    (activeAnswer?.chipSelections.length ?? 0) > 0 ||
    (activeAnswer?.freeText?.trim().length ?? 0) > 0;

  function toggleChip(chip: string) {
    if (!activeQuestion) return;
    setAnswers((prev) => {
      const existing = prev[activeQuestion.id] ?? {
        questionId: activeQuestion.id,
        chipSelections: [],
        freeText: null,
      };
      const chipSelections = existing.chipSelections.includes(chip)
        ? existing.chipSelections.filter((c) => c !== chip)
        : [...existing.chipSelections, chip];
      return { ...prev, [activeQuestion.id]: { ...existing, chipSelections } };
    });
  }

  function setFreeText(text: string) {
    if (!activeQuestion) return;
    setAnswers((prev) => {
      const existing = prev[activeQuestion.id] ?? {
        questionId: activeQuestion.id,
        chipSelections: [],
        freeText: null,
      };
      return {
        ...prev,
        [activeQuestion.id]: { ...existing, freeText: text.trim() || null },
      };
    });
  }

  function onNext() {
    if (activeIndex < questions.length - 1) {
      setActiveIndex(activeIndex + 1);
      return;
    }
    setPhase('self_assessment');
  }

  async function onSubmit() {
    if (!sessionId) return;
    setPhase('submitting');
    try {
      const result = await completeConfidenceCheck({
        sessionId,
        answers: questions.map(
          (q) =>
            answers[q.id] ?? {
              questionId: q.id,
              chipSelections: [],
              freeText: null,
            },
        ),
        selfAssessmentResponse: saResponse.trim() || null,
      });
      setSummary(result.overallSummary);
      setChanges(result.dimensionChanges);
      trackEvent({
        name: ANALYTICS_EVENTS.RECAL_CHECK_COMPLETED,
        properties: {
          ...getBaseProperties(),
          dimensionsShifted: result.dimensionChanges.length,
          confidenceDelta: result.dimensionChanges.reduce(
            (sum, c) => sum + (c.confidenceAfter - c.confidenceBefore),
            0,
          ),
        },
      });
      setPhase('results');
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : 'Could not save check');
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      />
      <div
        className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #111528, #0B0E1A)',
          border: '1px solid #2A2F4F',
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-content-muted hover:text-content transition-colors"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <X size={15} />
        </button>

        <div style={{ padding: '30px 28px' }}>
          {phase === 'loading' && (
            <div className="py-10 text-center text-content-muted text-sm">
              Preparing your 3–5 minute check…
            </div>
          )}

          {phase === 'error' && (
            <div className="py-10 text-center">
              <p className="text-red-400 text-sm mb-4">{error}</p>
              <button onClick={onClose} className="text-content-muted underline text-sm">
                Close
              </button>
            </div>
          )}

          {phase === 'questions' && activeQuestion && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
                Confidence Check · {activeIndex + 1} of {questions.length}
              </div>
              <h3 className="font-heading text-[17px] font-bold text-white mb-4 leading-snug">
                {activeQuestion.text}
              </h3>

              <div className="flex flex-wrap gap-2 mb-4">
                {activeQuestion.chips.map((chip) => {
                  const selected =
                    activeAnswer?.chipSelections.includes(chip) ?? false;
                  return (
                    <button
                      key={chip}
                      onClick={() => toggleChip(chip)}
                      className="text-xs font-medium rounded-full transition-all duration-200"
                      style={{
                        padding: '8px 14px',
                        background: selected
                          ? 'rgba(108,92,231,0.18)'
                          : 'rgba(255,255,255,0.03)',
                        border: selected
                          ? '1px solid rgba(108,92,231,0.5)'
                          : '1px solid rgba(255,255,255,0.08)',
                        color: selected ? '#A39BE0' : '#c0bdd4',
                      }}
                    >
                      {chip}
                    </button>
                  );
                })}
              </div>

              {activeQuestion.allow_other && (
                <textarea
                  placeholder="Other — tell me more (optional)"
                  value={activeAnswer?.freeText ?? ''}
                  onChange={(e) => setFreeText(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg text-sm p-3 mb-5"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#e1dfef',
                    resize: 'none',
                  }}
                />
              )}

              <div className="flex items-center justify-between">
                <div className="text-[11px] text-content-dim">
                  3–5 minutes · profile tightened by ±5
                </div>
                <button
                  onClick={onNext}
                  disabled={!hasSelection}
                  className="rounded-lg font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    padding: '9px 18px',
                    background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
                    color: '#fff',
                  }}
                >
                  {activeIndex < questions.length - 1 ? 'Next' : 'Self-assessment'}
                </button>
              </div>
            </>
          )}

          {phase === 'self_assessment' && selfAssessment && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
                One last step · Self-assessment
              </div>
              <div
                className="rounded-lg p-3 mb-4"
                style={{
                  background: 'rgba(108,92,231,0.06)',
                  border: '1px solid rgba(108,92,231,0.18)',
                }}
              >
                <p className="text-content-secondary text-[13px] leading-relaxed m-0">
                  {selfAssessment.profile_summary}
                </p>
              </div>
              <h3 className="font-heading text-[15px] font-bold text-white mb-3 leading-snug">
                {selfAssessment.prompt}
              </h3>
              <textarea
                placeholder="In your own words…"
                value={saResponse}
                onChange={(e) => setSaResponse(e.target.value)}
                rows={4}
                className="w-full rounded-lg text-sm p-3 mb-4"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#e1dfef',
                  resize: 'none',
                }}
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setPhase('questions')}
                  className="text-xs text-content-muted underline"
                >
                  Back
                </button>
                <button
                  onClick={onSubmit}
                  className="rounded-lg font-semibold text-sm"
                  style={{
                    padding: '9px 18px',
                    background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
                    color: '#fff',
                  }}
                >
                  Sharpen my profile <ArrowRight size={14} className="inline ml-1" />
                </button>
              </div>
            </>
          )}

          {phase === 'submitting' && (
            <div className="py-10 text-center text-content-muted text-sm">
              Applying a ±5 tightening pass to your profile…
            </div>
          )}

          {phase === 'results' && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
                Here’s what shifted
              </div>
              <p className="text-content-secondary text-sm mb-4 leading-relaxed">{summary}</p>

              <div className="space-y-2 mb-5 max-h-[260px] overflow-y-auto pr-1">
                {changes.length === 0 && (
                  <p className="text-content-muted text-[13px]">
                    No dimension shifts this cycle — your profile is holding steady.
                  </p>
                )}
                {changes.map((c) => {
                  const confDelta = c.confidenceAfter - c.confidenceBefore;
                  const scoreDelta = c.newScore - c.previousScore;
                  return (
                    <div
                      key={c.dimension}
                      className="rounded-lg p-3"
                      style={{
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[13px] font-semibold text-white">
                          {c.dimensionDisplayName}
                        </div>
                        <div className="text-[11px] font-code text-content-muted">
                          {scoreDelta !== 0 && (
                            <span className="mr-2">
                              score {scoreDelta > 0 ? '+' : ''}
                              {scoreDelta}
                            </span>
                          )}
                          <span
                            style={{ color: confDelta >= 0 ? '#A39BE0' : '#F0A500' }}
                          >
                            conf {confDelta > 0 ? '+' : ''}
                            {(confDelta * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <p className="text-[12px] text-content-muted leading-snug m-0">
                        {c.reasoning}
                      </p>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={onClose}
                className="w-full rounded-lg font-semibold text-sm py-2.5 text-white"
                style={{ background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)' }}
              >
                <Check size={14} className="inline mr-1" /> Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
